import invoiceExportHandler from "@NA/Handlers/InvoiceHistory/ADIInvoiceExportHandler";
import loadADIInvoices, {
    DefaultDate,
    invoiceParams,
    leadingZero,
} from "@NA/Handlers/InvoiceHistory/ADIInvoiceHistoryHandler";
import ExportIcon from "@NA/Icons/ExportIcon";
import { InvoiceParams } from "@NA/Services/ADIInvoiceService";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionHeader, AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import Button, { ButtonProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { priorStartDate } from "@NA/Utils/Utility";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import NpWarning from "@NA/Icons/NpWarning";

const mapStateToProps = (state: CustomApplicationState) => ({
    invoiceResults: state.ADIGlobalData.ADIInvoiceStore.invoiceResults,
    allLocationsInvoice: state.ADIGlobalData.ADIInvoiceStore.allLocationsInvoice,
    getInvoiceParameters: state.ADIGlobalData.ADIInvoiceStore.loadInvoiceParameters,
    settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
});

const mapDispatchToProps = {
    loadADIInvoices,
    invoiceExportHandler,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceSearchFiltersStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    filtersContainer?: GridContainerProps;
    poNumberGrid?: GridItemProps;
    statusGrid?: GridItemProps;
    DateGrid?: GridItemProps;
    searchGrid?: GridItemProps;
    clearGrid?: GridItemProps;
    dropdownContainer?: InjectableCss;
    dropdownValue?: InjectableCss;
    dropdownList?: InjectableCss;
    dropdownValues?: InjectableCss;
    dropdownLabel?: InjectableCss;
    icon?: TypographyProps;
    gridItem?: GridItemProps;
    Accordiion?: InjectableCss;
    defaultText?: InjectableCss;
    mtGridItem?: GridItemProps;
    searchBtn?: ButtonProps;
    clearBtn?: ButtonProps;
    datePickerStyles?: DatePickerPresentationProps;
    exportContainer?: GridContainerProps;
    exportImportButtonGridItem?: GridItemProps;
    searchResultGridItem?: GridItemProps;
    searchResult?: InjectableCss;
    exportImportButton?: InjectableCss;
    Icons?: InjectableCss;
    errorMsgWrapper?: InjectableCss;
}

export const tableStyles: InvoiceSearchFiltersStyles = {
    container: {
        css: css`
            width: 100%;
        `,
    },
    title: {
        as: "h2",
        css: css`
            font-size: ${getProp("theme.typography.h3.size")};
            color: ${ADIColors.darkBlue1};
            padding-bottom: 16px;
            border-bottom: 1px solid ${ADIColors.gray2};
            margin-top: 28px;
            margin-bottom: 24px;
            font-weight: bold;
            line-height: 1.18;
        `,
    },
    filtersContainer: {
        css: css`
            ${GridItemStyle} {
                padding: 15px 8px 15px 15px;
            }
             @media only screen and (min-width: 768px) and (max-width: 1080px) { max-width:100%; }    
        `,
    },
    poNumberGrid: {
        css: css`
            flex-basis: 22%;
            max-width: 22%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                    `,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                    `,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                    `,
                    null,
                ])}
        `,
    },
    gridItem: {
        css: css`
            flex-direction: column;
            flex-basis: 13%;
            max-width: 13%;
            &:first-child {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        null,
                    ])}
            }
            &.invoiceStatus {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        null,
                    ])}
            }
            &.dateRange {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        null,
                    ])}
            }
            &.startDate {
                .react-datetime-picker__button:enabled {
                    svg {
                        transform: translate(10px, 0px);
                    }
                        &:focus{
                        span{
                           outline:none !important;
                        }
                        }
                }
                    
                .react-datetime-picker--enabled {
                    z-index: 1;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        null,
                    ])}
            }
            &.endDate {
                .react-datetime-picker--enabled {
                    z-index: 1;
                }

                .react-datetime-picker__button:enabled {
                    svg {
                        transform: translate(10px, 0px);
                    }
                          &:focus{
                        span{
                        outline:none !important;
                        }
                        }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        null,
                    ])}
            }
            &.searchBtn {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        null,
                    ])}
            }
            &.clearBtn {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        css`
                            flex-basis: 25%;
                            max-width: 25%;
                        `,
                        null,
                    ])}
            }
            .react-datetime-picker {
                height: 40px !important;
                transform: translateY(-2px);

                :focus,
                :focus-within {
                    svg {
                        transform: translate(10px, 0px);
                        outline: 0 !important;
                    }
                }
            }
        `,
    },
    Accordiion: {
        css: css`
             position: relative;
            > ${AccordionSectionHeader} {
                border-radius: 12px 12px 0 0;
                > Button {
                    background: ${getColor("common.background")};
                    border: none;
                    border-radius: 12px 12px 0 0;
                    border-bottom: 1px solid ${getColor("common.border")};
                    > ${TypographyStyle} {
                        color: ${ADIColors.darkBlue1};
                        text-transform: uppercase;
                    }
                }
                ${IconWrapper} {
                    margin: 0 !important;
                 }
            }
            > ${AccordionSectionPanel} {
                background: ${getColor("common.background")};
                border: none;
            }
        `,
    },
    defaultText: {
        css: css`
        display: none;
        ${({ theme }) => { return breakpointMediaQueries(
            theme, [css` 
            position: absolute;
            top: 8px;
            right: 35px;
            font-size: 16px;
            left: auto;
            font-weight: bold;
            display: block;
            color: #666; `,],"max",); 
        }}
        
        `,
    },
    mtGridItem: {
        css: css`
            flex-direction: column;
            flex-basis: 100%;
            max-width: 100%;
            padding: 10px 0;
            &.datePicker {
                flex-basis: 100%;
                max-width: 100%;
                flex-direction: row;
                > div {
                    margin-right: 8px;
                }
            }
            .react-datetime-picker {
                height: 40px !important;
                transform: translateY(-2px);

                :focus,
                :focus-within {
                    svg {
                        transform: translate(10px, 0px);
                        outline: 0 !important;
                    }
                }
            }
        `,
    },

    dropdownLabel: {
        css: css`
            font-weight: 700;
            text-transform: capitalize;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            margin-bottom: 6px;
        `,
    },
    dropdownContainer: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    dropdownValue: {
        css: css`
            width: 100%;
            border: 1px solid;
            border-radius: 6px;
            padding: 9px 9px 9px 15px;
            height: 40px;
            background: ${ADIColors.white};
            border: 1px solid ${ADIColors.gray3};
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            cursor: pointer;
            position: relative;
            z-index: 10;
            display: flex;
            align-items: normal;
            justify-content: space-between;
        `,
    },
    dropdownList: {
        css: css`
            border-radius: 0 0 7px 7px;
            box-shadow: 1px 2px 14px 4px rgba(57, 57, 58, 0.25);
            background: ${ADIColors.white};
            position: absolute;
            width: 100%;
            top: 90%;
            z-index: 12;
        `,
    },
    dropdownValues: {
        css: css`
            min-height: 40px;
            border-bottom: 1px solid ${ADIColors.gray3};
            font-weight: 600;
            padding: 13px 12px;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            cursor: pointer;

            &:last-child {
                border-bottom: 0;
                border-radius: 0 0 7px 7px;
            }

            &.active,
            &:hover {
                background-color: ${ADIColors.blue1};
                color: ${ADIColors.white};
            }
        `,
    },
    icon: {
        css: css`
            transform: translateY(-1px) scale(0.9);
            svg {
                stroke: ${ADIColors.darkBlue1};
            }
        `,
    },
    searchBtn: {
        css: css`
            margin-top: 26px;
            width: 100%;
            border-radius: 4px;
            :hover,
            :disabled {
                background-color: ${getColor("primary")};
                border-color: ${getColor("primary")};
            }
        `,
    },
    clearBtn: {
        css: css`
            margin-top: 26px;
            width: 100%;
            border-radius: 4px;
            background-color: ${ADIColors.white};
            border-color: ${getColor("primary")};
            span {
                color: ${getColor("primary")};
            }

            :hover,
            :disabled {
                background-color: ${ADIColors.white};
                border-color: ${getColor("primary")};
                span {
                    color: ${getColor("primary")};
                }
            }
        `,
    },
    datePickerStyles: {
        calendarIconProps: {
            css: css`
            span{
                outline:none !important;
            }
                svg {
                    transform: translate(10px, -7px);
                    @media (max-width: 768px) {
                        transform: translate(10px, 0px);
                    }
                }
            `,
            color: "primary",
        },
    },
    exportContainer: {
        css: css`
            background-color: transparent;
            > ${GridOffset} {
                width: 100%;
                margin: auto;
            }
        `,
    },
    searchResultGridItem: {
        css: css`
            flex-basis: 48%;
            max-width: 48%;
            padding: 8px 0 0;
            align-items: center;
        `,
    },
    searchResult: {
        css: css`
            font-size: 20px;
            font-weight: 600;
            line-height: 1.25;
            color: ${ADIColors.darkBlue1};
            margin: 0 12px 0 0;
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    exportImportButtonGridItem: {
        css: css`
            flex-basis: 52%;
            max-width: 52%;
            padding: 8px 0 0;
            justify-content: flex-end;
        `,
    },
    exportImportButton: {
        css: css`
            background: transparent;
            color: ${getColor("primary")};
            font-size: 14px;
            font-weight: bold;
            font-style: normal;
            line-height: normal;
            outline: none;
            border: none;
            display: flex;
            align-items: center;
            padding: 9px 10px;
            margin-left: 4px;
            flex-basis: 50%;
            max-width: 50%;
            &:hover {
                text-decoration: underline;
                background: transparent;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    Icons: {
        css: css`
            height: 17px;
            width: 17px;
            margin: 1px 6px 1px 0;
            > svg {
                width: 17px;
                height: 17px;
            }
        `,
    },
    errorMsgWrapper: {
        css: css`
            width: 100%;
            padding-top: 20px;
            font-weight: 700;
            display: flex;
            justify-content: center;
            span {
                margin-right: 10px;
            }
        `,
    },    
};

const styles = tableStyles;

function useOutsideAlerter(ref: any, setaction: any) {
    React.useEffect(() => {
        function handleClickOutside(event: any) {
            if (ref.current && !ref.current.contains(event.target)) {
                setaction(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref]);
}

function removeEmpty(obj: InvoiceParams) {
    return Object.entries(obj)
        .filter(([_, v]) => v != null)
        .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
}

const InvoiceSearchFilters = ({
    invoiceResults,
    allLocationsInvoice,
    loadADIInvoices,
    getInvoiceParameters,
    invoiceExportHandler,
    settingsCollection,  
}: Props) => {
    const listStatus = sessionStorage.getItem("orders_last_status") ? sessionStorage.getItem("orders_last_status") : null;
    const [poNumber, setPoNumber] = React.useState("");
    //const [selectedStatus, setSelectedStatus] = React.useState("Open");
    const [showDateRange, setShowDateRange] = React.useState<boolean>(true);
    const [selectedStatus, setSelectedStatus] = React.useState(listStatus == null ? "Open" : listStatus);
    const [openStatus, setOpenStatus] = React.useState(false);
    const [selectedDateRange, setSeletecDateRange] = React.useState("Last 180 days");
    const [openDates, setOpenDates] = React.useState(false);
    const [fromDate, setFromDate] = React.useState(getInvoiceParameters.fromDate);
    const [toDate, setToDate] = React.useState(getInvoiceParameters.toDate);
    const [isMobile, setIsMobile] = React.useState(false);
    const adiInvoiceHistorySettingSettings = settingsCollection.adiInvoiceHistorySettingSettings;
    const {dpdFilterData,dpdFilterType}=getInvoiceParameters

    React.useEffect(() => {
       getInvoiceParameters?.dpdFilterType === "dpd" ? setShowDateRange(true): setShowDateRange(false);
    },[dpdFilterType,dpdFilterData])

    React.useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            sessionStorage.setItem("orders_last_status", "Open");
        };
        window.addEventListener('beforeunload', handleBeforeUnload);


        const onWindowResize = () => {
            setIsMobile(window.innerWidth < 700);
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);
    const tzOffset = new Date().getTimezoneOffset() * 60000;
    
     const convertStringToDate = (dateStr?: string) => {
        return dateStr ? new Date(new Date(dateStr).getTime() + tzOffset) : undefined;
    };

    const convertDateToString = (date?: Date) => {
        return date ? new Date(date.getTime() - tzOffset).toISOString().split("T")[0] : "";
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (typeof selectedDay !== undefined) {
            setFromDate(convertDateToString(selectedDay));
        }
    };
    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (typeof selectedDay !== undefined) {
            setToDate(convertDateToString(selectedDay));
        }
    };

    const changeFormat = (Date2: string) => {
        let date1 = new Date(Date2);
        return leadingZero(date1.getMonth() + 1) + "/" + leadingZero(date1.getDate()) + "/" + date1.getFullYear();
    };

    const statusRef = React.useRef(null);
    const dateRef = React.useRef(null);
    useOutsideAlerter(statusRef, setOpenStatus);
    useOutsideAlerter(dateRef, setOpenDates);

    const handleStatusChange = (value: string) => {
        setSelectedStatus(value);
        sessionStorage.setItem("orders_last_status", value);
        setOpenStatus(false);
        setShowDateRange(true);
        if (getInvoiceParameters) {
            let selectedStatus = value;
            loadADIInvoices({
                ...getInvoiceParameters,
                poNumber: "",
                invoiceType: selectedStatus === "Open" ? "1" : "0",
                invoiceTypeDesc: selectedStatus,
                toDate: DefaultDate(false, false) || "",
                fromDate: DefaultDate(true, false) || "",
                dateRange: "180",
                dateRangeDesc: "Last 180 days",
                showOpenOnly: selectedStatus === "Open" ? true : false,
            });
        }
    };

    const handleDateRange = (value: string) => {
        setSeletecDateRange(value);
        setOpenDates(false);
    };

    const handleSearch = () => {
        if (getInvoiceParameters) {
            let selectedFromDate;
            let selectedToDate;
            if (selectedDateRange !== "Custom") {
                selectedFromDate = changeFormat(
                    DefaultDate(true, false, Number(selectedDateRange.split(" ").splice(1)[0])) || "",
                );
                selectedToDate = changeFormat(
                    DefaultDate(false, false, Number(selectedDateRange.split(" ").splice(1)[0])) || "",
                );
            } else {
                selectedFromDate = changeFormat(fromDate || "");
                selectedToDate = changeFormat(toDate || "");
            }

            loadADIInvoices({
                ...getInvoiceParameters,
                invoiceNumber: poNumber ? poNumber : "",
                dpdFilterType: "dpd",
                dpdFilterData: "0",
                selectedpage: 1,
                dateRange: selectedDateRange.split(" ").splice(1).join(""),
                dateRangeDesc: selectedDateRange,
                fromDate: selectedFromDate,
                toDate: selectedToDate,
            });
        }
    };

    const clearSearch = () => {
        let todaydate= new Date();
        let priorDate = new Date(new Date().setDate(todaydate.getDate() - 180));
        setPoNumber("");
        setSeletecDateRange("Last 180 days");  
        setSelectedStatus("Open");
        sessionStorage.setItem("orders_last_status", "Open");
        setFromDate(convertDateToString(priorDate));
        setToDate(convertDateToString(todaydate));
        if (getInvoiceParameters) {
            loadADIInvoices({
                ...invoiceParams,
                customerSequence: getInvoiceParameters.customerSequence,
                dateRange: getInvoiceParameters.dateRange === "180days" ? "180" : "1",            
            });
        }
    };

    if (allLocationsInvoice) {
        return null;
    }
    if (!invoiceResults) {
        return null;
    }
    const handleExport = () => {
        if (invoiceResults) invoiceExportHandler({ ...getInvoiceParameters });
    };

    return (
        <>
            {!isMobile ? (
                <StyledWrapper {...styles.container}>
                    <Typography {...styles.title}>{translate("Invoices Search")}</Typography>

                    <GridContainer {...styles.filtersContainer}>
                        <GridItem {...styles.poNumberGrid}>
                            <TextField
                                label={translate("PO Number, Invoice No., Reference No.")}
                                placeholder="Enter number here..."
                                type="text"
                                value={poNumber}
                                onChange={e => setPoNumber(e.target.value)}
                                maxLength={25}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem} className="invoiceStatus">
                            <StyledWrapper as="label" {...styles.dropdownLabel}>
                                {translate("Invoice Status")}
                            </StyledWrapper>
                            <StyledWrapper {...styles.dropdownContainer} ref={statusRef}>
                                <StyledWrapper {...styles.dropdownValue} onClick={() => setOpenStatus(!openStatus)}>
                                    {selectedStatus}
                                    <Typography {...styles.icon}>
                                        <ChevronDown />
                                    </Typography>
                                </StyledWrapper>
                                {openStatus ? (
                                    <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => handleStatusChange("Open")}
                                            className={selectedStatus === "Open" ? "active" : "normal"}
                                        >
                                            {translate("Open")}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => handleStatusChange("Recently Paid")}
                                            className={selectedStatus === "Recently Paid" ? "active" : "normal"}
                                        >
                                            {translate("Recently Paid")}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                        </GridItem>
                        {showDateRange && selectedStatus !== "Recently Paid" ? (
                            <GridItem {...styles.gridItem} className="dateRange">
                                <StyledWrapper as="label" {...styles.dropdownLabel}>
                                    {translate("Date Range")}
                                </StyledWrapper>
                                <StyledWrapper {...styles.dropdownContainer} ref={dateRef}>
                                    <StyledWrapper {...styles.dropdownValue} onClick={() => setOpenDates(!openDates)}>
                                        {selectedDateRange}
                                        <Typography {...styles.icon}>
                                            <ChevronDown />
                                        </Typography>
                                    </StyledWrapper>
                                    {openDates ? (
                                        <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                            <StyledWrapper
                                                {...styles.dropdownValues}
                                                as={"li"}
                                                onClick={() => handleDateRange("Last 30 days")}
                                                className={selectedDateRange === "Last 30 days" ? "active" : "normal"}
                                            >
                                                {translate("Last 30 days")}
                                            </StyledWrapper>

                                            <StyledWrapper
                                                {...styles.dropdownValues}
                                                as={"li"}
                                                onClick={() => handleDateRange("Last 60 days")}
                                                className={selectedDateRange === "Last 60 days" ? "active" : "normal"}
                                            >
                                                {translate("Last 60 days")}
                                            </StyledWrapper>

                                            <StyledWrapper
                                                {...styles.dropdownValues}
                                                as={"li"}
                                                onClick={() => handleDateRange("Last 90 days")}
                                                className={selectedDateRange === "Last 90 days" ? "active" : "normal"}
                                            >
                                                {translate("Last 90 days")}
                                            </StyledWrapper>

                                            <StyledWrapper
                                                {...styles.dropdownValues}
                                                as={"li"}
                                                onClick={() => handleDateRange("Last 120 days")}
                                                className={selectedDateRange === "Last 120 days" ? "active" : "normal"}
                                            >
                                                {translate("Last 120 days")}
                                            </StyledWrapper>

                                            <StyledWrapper
                                                {...styles.dropdownValues}
                                                as={"li"}
                                                onClick={() => handleDateRange("Last 180 days")}
                                                className={selectedDateRange === "Last 180 days" ? "active" : "normal"}
                                            >
                                                {translate("Last 180 days")}
                                            </StyledWrapper>

                                            <StyledWrapper
                                                {...styles.dropdownValues}
                                                as={"li"}
                                                onClick={() => handleDateRange("Custom")}
                                                className={selectedDateRange === "Custom" ? "active" : "normal"}
                                            >
                                                {translate("Custom")}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ) : null}
                                </StyledWrapper>
                            </GridItem>
                        ) : null}                       

                        {selectedDateRange === "Custom" && selectedStatus !== "Recently Paid" ? (
                            <>
                                <GridItem {...styles.gridItem} className="startDate">
                                    <DatePicker
                                        {...styles.datePickerStyles}
                                        label={translate("Start Date")}
                                        selectedDay={convertStringToDate(fromDate)}
                                        onDayChange={fromDateChangeHandler}
                                        format="MM/dd/y"
                                        dateTimePickerProps={{
                                            clearIcon: null,
                                            maxDate: convertStringToDate(toDate),
                                            minDate: convertStringToDate(priorStartDate(adiInvoiceHistorySettingSettings.invoiceHistory_MaxDateRangeInDays).toString()),
                                        }}
                                    />
                                </GridItem>
                                <GridItem {...styles.gridItem} className="endDate">
                                    <DatePicker
                                        {...styles.datePickerStyles}
                                        label={translate("End Date")}
                                        selectedDay={convertStringToDate(toDate)}
                                        onDayChange={toDateChangeHandler}
                                        format="MM/dd/y"
                                        dateTimePickerProps={{
                                            clearIcon: null,
                                            minDate: convertStringToDate(fromDate), 
                                        }}
                                    />
                                </GridItem>
                            </>
                        ) : null}

                        <GridItem {...styles.gridItem} className="searchBtn">
                            <Button {...styles.searchBtn} onClick={handleSearch}>
                                {translate("Search")}
                            </Button>
                        </GridItem>
                        <GridItem {...styles.gridItem} className="clearBtn">
                            <Button {...styles.clearBtn} onClick={clearSearch}>
                                {translate("Clear")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            ) : (
                <>
                    <GridContainer {...styles.exportContainer}>
                        <GridItem {...styles.searchResultGridItem}>
                            <Typography {...styles.searchResult}>{translate(`Open Invoices`)}</Typography>
                        </GridItem>
                        <GridItem {...styles.exportImportButtonGridItem}>
                            <Button shape="rounded" {...styles.exportImportButton} onClick={handleExport}>
                                <Icon src={ExportIcon} color="primary" {...styles.Icons} />
                                {translate("Export")}
                            </Button>
                        </GridItem>
                    </GridContainer>

                    <Accordion headingLevel={4} {...styles.Accordiion}>
                        <Typography {...styles.defaultText}> (Default)</Typography>
                        <AccordionSection title="Filter" >
                            <GridItem {...styles.mtGridItem}>
                                <StyledWrapper as="label" {...styles.dropdownLabel}>
                                    {translate("Invoice Status")}
                                </StyledWrapper>
                                <StyledWrapper {...styles.dropdownContainer} ref={statusRef}>
                                    <StyledWrapper {...styles.dropdownValue} onClick={() => setOpenStatus(!openStatus)}>
                                        {selectedStatus}
                                        <Typography {...styles.icon}>
                                            <ChevronDown />
                                        </Typography>
                                    </StyledWrapper>
                                    {openStatus ? (
                                        <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                            <StyledWrapper
                                                {...styles.dropdownValues}
                                                as={"li"}
                                                onClick={() => handleStatusChange("Open")}
                                                className={selectedStatus === "Open" ? "active" : "normal"}
                                            >
                                                {translate("Open")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.dropdownValues}
                                                as={"li"}
                                                onClick={() => handleStatusChange("Recently Paid")}
                                                className={selectedStatus === "Recently Paid" ? "active" : "normal"}
                                            >
                                                {translate("Recently Paid")}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ) : null}
                                </StyledWrapper>
                            </GridItem>
                            {selectedStatus !== "Recently Paid" ? (
                                <GridItem {...styles.mtGridItem}>
                                    <StyledWrapper as="label" {...styles.dropdownLabel}>
                                        {translate("Date Range")}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.dropdownContainer} ref={dateRef}>
                                        <StyledWrapper
                                            {...styles.dropdownValue}
                                            onClick={() => setOpenDates(!openDates)}
                                        >
                                            {selectedDateRange}
                                            <Typography {...styles.icon}>
                                                <ChevronDown />
                                            </Typography>
                                        </StyledWrapper>
                                        {openDates ? (
                                            <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => handleDateRange("Last 30 days")}
                                                    className={
                                                        selectedDateRange === "Last 30 days" ? "active" : "normal"
                                                    }
                                                >
                                                    {translate("Last 30 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => handleDateRange("Last 60 days")}
                                                    className={
                                                        selectedDateRange === "Last 60 days" ? "active" : "normal"
                                                    }
                                                >
                                                    {translate("Last 60 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => handleDateRange("Last 90 days")}
                                                    className={
                                                        selectedDateRange === "Last 90 days" ? "active" : "normal"
                                                    }
                                                >
                                                    {translate("Last 90 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => handleDateRange("Last 120 days")}
                                                    className={
                                                        selectedDateRange === "Last 120 days" ? "active" : "normal"
                                                    }
                                                >
                                                    {translate("Last 120 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => handleDateRange("Last 180 days")}
                                                    className={
                                                        selectedDateRange === "Last 180 days" ? "active" : "normal"
                                                    }
                                                >
                                                    {translate("Last 180 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => handleDateRange("Custom")}
                                                    className={selectedDateRange === "Custom" ? "active" : "normal"}
                                                >
                                                    {translate("Custom")}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ) : null}
                                    </StyledWrapper>
                                </GridItem>
                            ) : null}

                            {selectedDateRange === "Custom" && selectedStatus !== "Recently Paid" ? (
                                <>
                                    <GridItem {...styles.mtGridItem} className="datePicker">
                                        <DatePicker
                                            {...styles.datePickerStyles}
                                            label={translate("Start Date")}
                                            selectedDay={convertStringToDate(fromDate)}
                                            onDayChange={fromDateChangeHandler}
                                            format={"MM/dd/y"}
                                            dateTimePickerProps={{
                                                clearIcon: null,
                                                maxDate: convertStringToDate(toDate),
                                               minDate: convertStringToDate(priorStartDate(adiInvoiceHistorySettingSettings.invoiceHistory_MaxDateRangeInDays).toString()),
                                            }}
                                        />
                                        <DatePicker
                                            {...styles.datePickerStyles}
                                            label={translate("End Date")}
                                            selectedDay={convertStringToDate(toDate)}
                                            onDayChange={toDateChangeHandler}
                                            format={"MM/dd/y"}
                                            dateTimePickerProps={{
                                                clearIcon: null,
                                                minDate: convertStringToDate(fromDate),                                               
                                            }}
                                        />
                                    </GridItem>
                                </>
                            ) : null}

                            <GridItem {...styles.mtGridItem} className="searchButton">
                                <Button {...styles.searchBtn} onClick={handleSearch}>
                                    {translate("Apply")}
                                </Button>
                            </GridItem>
                        </AccordionSection>
                    </Accordion>
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceSearchFilters),
    definition: {
        group: "Invoice History",
        displayName: "Invoice Search Filters",
    },
};

export default widgetModule;
